import { DownloadOutlined, EyeOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Modal } from "antd"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { downloadBlob } from "../../../../../../../../utils/download-file.ts"

interface GanttPdfExportModalProps {
  open: boolean
  blob: Blob | null
  scaledDown: boolean
  projectName?: string
  onClose: () => void
}

export function GanttPdfExportModal({
  open,
  blob,
  scaledDown,
  projectName,
  onClose
}: GanttPdfExportModalProps) {
  const { t } = useTranslation()
  const objectUrlRef = useRef<string | null>(null)

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
  }

  // Revokes whatever object URL this modal is currently holding when it
  // unmounts, on top of the explicit revokes on close / before a repeat View.
  useEffect(() => () => revokeObjectUrl(), [])

  const filename = `Milestone-Schedule-${(projectName || "Project").replace(/[^\w-]+/g, "-")}.pdf`

  const handleView = () => {
    if (!blob) return
    // A repeat View click would otherwise leak the previous URL — the new
    // tab needs its own, so the old one is revoked first, never reused.
    revokeObjectUrl()
    objectUrlRef.current = URL.createObjectURL(blob)
    window.open(objectUrlRef.current, "_blank", "noopener")
  }

  const handleDownload = () => {
    if (!blob) return
    downloadBlob(blob, filename)
  }

  const handleClose = () => {
    revokeObjectUrl()
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={t("milestonesGanttPdf.modalTitle")}
      destroyOnHidden
    >
      {scaledDown && (
        <Alert type="info" showIcon message={t("milestonesGanttPdf.scaledNote")} className="mb-3" />
      )}

      <Flex gap="small" wrap>
        <Button icon={<EyeOutlined />} onClick={handleView} aria-label={t("milestonesGanttPdf.viewAction")}>
          {t("milestonesGanttPdf.viewAction")}
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleDownload} aria-label={t("milestonesGanttPdf.downloadAction")}>
          {t("milestonesGanttPdf.downloadAction")}
        </Button>
      </Flex>
    </Modal>
  )
}
